import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  CreditCard, ArrowClockwise, DownloadSimple, UploadSimple, Eye, EyeSlash, Copy, Trash,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import AdminCardUploadDialog from '../components/vendor-cards/AdminCardUploadDialog';
import SearchBar from '../components/SearchBar';

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}
function mask(num) {
  if (!num) return '';
  const s = String(num);
  return s.length <= 4 ? s : `${s.slice(0, 4)} •••• •••• ${s.slice(-4)}`;
}

/** Unified page: Card Vendor sees own cards, Admin can view any vendor's via ?vendor_id=X
 *  CA/Assistant/Staff blocked by backend 403.
 */
export default function CardVendorCardsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const queryVendorId = params.get('vendor_id');

  const isCardVendor = user?.role === 'card_vendor';
  const isAdmin = ['admin', 'owner'].includes(user?.role);
  const vendorId = isCardVendor ? user.id : queryVendorId;

  const [cards, setCards] = useState([]);
  const [summary, setSummary] = useState({ total: 0, used: 0, unused: 0, total_face: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showSecrets, setShowSecrets] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [vendorMeta, setVendorMeta] = useState(null);

  const load = useCallback(async () => {
    if (!vendorId && !isCardVendor) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (vendorId && isAdmin) params.set('vendor_id', vendorId);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search) params.set('search', search);
      const [c, s] = await Promise.all([
        api.get(`/vendor-cards/?${params.toString()}`),
        vendorId ? api.get(`/vendor-cards/summary?vendor_id=${vendorId}`) : Promise.resolve({ data: null }),
      ]);
      setCards(c.data || []);
      if (s.data) setSummary(s.data);
      if (isAdmin && vendorId && !vendorMeta) {
        try {
          const v = await api.get(`/vendors/${vendorId}`);
          setVendorMeta(v.data);
        } catch { /* ignore */ }
      }
    } catch (err) {
      if (err?.response?.status === 403) toast.error('Access denied');
      else toast.error(err?.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [vendorId, isCardVendor, isAdmin, statusFilter, search, vendorMeta]);

  useEffect(() => { load(); }, [load]);

  const toggleUsed = async (cardId, newVal, cardMeta) => {
    const last4 = cardMeta?.card_number_last4 || '';
    const msg = newVal
      ? `Card ****${last4} ko USED mark karein? (Confirm karne ke baad status change hoga)`
      : `Card ****${last4} ko UNUSED mark karein?`;
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/vendor-cards/${cardId}/used`, { is_used: newVal });
      setCards((prev) => prev.map((c) => c.id === cardId ? { ...c, is_used: newVal } : c));
      toast.success(newVal ? 'Marked as Used ✓' : 'Marked as Unused');
      const s = await api.get(`/vendor-cards/summary?vendor_id=${vendorId}`);
      setSummary(s.data);
    } catch (err) { toast.error('Update failed'); }
  };

  const bulkMark = async (newVal) => {
    const rows = cards.filter((c) => c.is_used !== newVal);
    if (!rows.length) { toast.info('Koi change needed nahi'); return; }
    const label = newVal ? 'USED' : 'UNUSED';
    if (!window.confirm(`${rows.length} card(s) ko ${label} mark karein?`)) return;
    try {
      await Promise.all(rows.map((c) => api.patch(`/vendor-cards/${c.id}/used`, { is_used: newVal })));
      toast.success(`${rows.length} cards ${label} marked`);
      await load();
    } catch { toast.error('Bulk update failed'); }
  };

  const deleteCard = async (cardId) => {
    if (!window.confirm('Yeh card delete kar dein? Ledger me reverse credit auto ban jayega.')) return;
    try {
      const r = await api.delete(`/vendor-cards/${cardId}`);
      toast.success(`Deleted. Reverse credit: ${fmt(r.data.reversed_amount)}`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  const downloadExport = (format, forcedStatus) => {
    const API = window.location.origin;
    const p = new URLSearchParams();
    p.set('format', format);
    if (vendorId && isAdmin) p.set('vendor_id', vendorId);
    const useStatus = forcedStatus || (statusFilter !== 'all' ? statusFilter : null);
    if (useStatus) p.set('status', useStatus);
    const a = document.createElement('a');
    a.href = `${API}/api/vendor-cards/export?${p.toString()}`;
    a.target = '_blank';
    document.body.appendChild(a); a.click(); a.remove();
  };

  const copyCard = (c) => {
    const txt = [c.card_holder, c.card_number, c.cvv && `CVV ${c.cvv}`, c.expiry, c.pin && `Pin ${c.pin}`, `Rs ${c.card_limit}`]
      .filter(Boolean).join('\n');
    navigator.clipboard?.writeText(txt).then(() => toast.success('Copied'));
  };

  if (!vendorId && !isCardVendor) {
    return <div className="p-8 text-center text-sm text-gray-500">Admin: vendor_id query param chahiye. Vendor detail page se "View Cards" click karein.</div>;
  }

  return (
    <div className="space-y-5" data-testid="card-vendor-cards-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard size={26} weight="duotone" className="text-indigo-600" />
          {isCardVendor ? 'My Cards' : `Cards — ${vendorMeta?.name || '...'}`}
          <span className="text-sm font-normal text-gray-500 ml-1">({summary.total})</span>
        </h1>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setShowSecrets((v) => !v)} className="gap-1" data-testid="toggle-secrets-btn">
            {showSecrets ? <EyeSlash size={14} /> : <Eye size={14} />}
            {showSecrets ? 'Hide Details' : 'Show Details'}
          </Button>
          <Button size="sm" variant="outline" onClick={load} className="gap-1"><ArrowClockwise size={14} /> Refresh</Button>
          <Button size="sm" onClick={() => downloadExport('xlsx')} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="export-xlsx-btn">
            <DownloadSimple size={14} /> Excel
          </Button>
          <Button size="sm" onClick={() => downloadExport('txt')} variant="outline" className="gap-1" data-testid="export-txt-btn">
            <DownloadSimple size={14} /> TXT
          </Button>
          {isAdmin && vendorMeta?.vendor_type === 'card' && (
            <Button size="sm" onClick={() => setShowUpload(true)} className="bg-indigo-600 hover:bg-indigo-700 gap-1" data-testid="add-cards-btn">
              <UploadSimple size={14} /> Add Cards
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-indigo-700 font-semibold">Total Cards</div>
          <div className="text-2xl font-bold text-indigo-900">{summary.total}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 group relative">
          <div className="text-[11px] uppercase text-emerald-700 font-semibold flex items-center justify-between">
            Unused
            <button
              onClick={() => downloadExport('xlsx', 'unused')}
              className="p-1 rounded hover:bg-emerald-100 text-emerald-700"
              title="Download Unused cards (Excel)"
              data-testid="quick-unused-xlsx"
            >
              <DownloadSimple size={12} weight="bold" />
            </button>
          </div>
          <div className="text-2xl font-bold text-emerald-900">{summary.unused}</div>
          <div className="text-[9px] text-emerald-600 mt-0.5">Click ⬇ to download just unused</div>
        </div>
        <div className="bg-gray-100 border border-gray-200 rounded-lg p-3 group relative">
          <div className="text-[11px] uppercase text-gray-700 font-semibold flex items-center justify-between">
            Used
            <button
              onClick={() => downloadExport('xlsx', 'used')}
              className="p-1 rounded hover:bg-gray-200 text-gray-700"
              title="Download Used cards (Excel)"
              data-testid="quick-used-xlsx"
            >
              <DownloadSimple size={12} weight="bold" />
            </button>
          </div>
          <div className="text-2xl font-bold text-gray-900">{summary.used}</div>
          <div className="text-[9px] text-gray-600 mt-0.5">Click ⬇ to download just used</div>
        </div>
        <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-fuchsia-700 font-semibold">Total Limit</div>
          <div className="text-xl font-bold text-fuchsia-900">{fmt(summary.total_face)}</div>
        </div>
      </div>

      {/* Filter row */}
      <div className="bg-white border rounded-lg p-3 flex gap-2 flex-wrap items-center">
        {['all', 'unused', 'used'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs font-semibold rounded border ${statusFilter === s ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-gray-300 hover:bg-gray-50'}`}
            data-testid={`filter-${s}`}
          >
            {s.toUpperCase()}
          </button>
        ))}
        <div className="h-5 w-px bg-gray-300 mx-1" />
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs border-emerald-400 text-emerald-700 hover:bg-emerald-50" onClick={() => bulkMark(false)} data-testid="bulk-mark-unused">
          Mark All UNUSED
        </Button>
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs border-gray-400 text-gray-700 hover:bg-gray-100" onClick={() => bulkMark(true)} data-testid="bulk-mark-used">
          Mark All USED
        </Button>
        <div className="ml-auto">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Card number / holder search..."
            matchCount={cards.length}
            noun="cards"
            testId="card-vendor-search"
            inputClassName="pl-8 pr-7 h-8 text-xs w-64"
          />
        </div>
      </div>

      {/* Cards table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-2 bg-indigo-50 border-b text-xs flex justify-between flex-wrap gap-2">
          <span className="text-indigo-900">Showing <b>{cards.length}</b> cards</span>
        </div>
        {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        : cards.length === 0 ? <div className="p-8 text-center text-sm text-gray-500">Koi card nahi hai</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left w-10">#</th>
                  <th className="px-3 py-2 text-left">Holder</th>
                  <th className="px-3 py-2 text-left">Card Number</th>
                  <th className="px-3 py-2 text-left">CVV</th>
                  <th className="px-3 py-2 text-left">Expiry</th>
                  <th className="px-3 py-2 text-left">PIN</th>
                  <th className="px-3 py-2 text-right">Limit</th>
                  <th className="px-3 py-2 text-left">Uploaded</th>
                  <th className="px-3 py-2 text-center">Used?</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((c, i) => (
                  <tr key={c.id} className={`border-t hover:bg-gray-50 ${c.is_used ? 'opacity-70' : ''}`} data-testid={`card-row-${c.id}`}>
                    <td className="px-3 py-1.5 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-1.5 text-xs font-medium">{c.card_holder || '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{showSecrets ? c.card_number : mask(c.card_number)}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{showSecrets ? (c.cvv || '—') : '•••'}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{c.expiry || '—'}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">{showSecrets ? (c.pin || '—') : (c.pin ? '••••' : '—')}</td>
                    <td className="px-3 py-1.5 text-right font-semibold">{fmt(c.card_limit)}</td>
                    <td className="px-3 py-1.5 text-[10px] text-gray-500">
                      <div>{fmtDate(c.uploaded_at)}</div>
                      <div className="text-gray-400">{c.uploaded_by_name}</div>
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={!!c.is_used}
                        onChange={(e) => toggleUsed(c.id, e.target.checked, c)}
                        className="w-4 h-4 cursor-pointer accent-indigo-600"
                        data-testid={`toggle-used-${c.id}`}
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex gap-1 justify-end">
                        <button
                          className="p-1 rounded hover:bg-gray-100 text-gray-500"
                          onClick={() => copyCard(c)}
                          title="Copy"
                          data-testid={`copy-${c.id}`}
                        >
                          <Copy size={14} />
                        </button>
                        {isAdmin && (
                          <button
                            className="p-1 rounded hover:bg-red-50 text-red-500"
                            onClick={() => deleteCard(c.id)}
                            title="Delete"
                            data-testid={`delete-${c.id}`}
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isAdmin && vendorId && (
        <AdminCardUploadDialog
          open={showUpload}
          onOpenChange={setShowUpload}
          vendorId={vendorId}
          vendorName={vendorMeta?.name || 'Card Vendor'}
          defaultDiscount={vendorMeta?.discount_percent || 0}
          onDone={load}
        />
      )}
    </div>
  );
}
